import {
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from "recharts";

import { CHART_COLORS, brPt } from "../../lib/format";

const AXIS = "var(--color-ink-faint)";
const GRID = "var(--color-border)";

const tooltipStyle = {
  background: "color-mix(in srgb, var(--color-surface) 96%, transparent)",
  border: "1px solid var(--color-border-strong)",
  borderRadius: 14,
  fontSize: 12,
  color: "var(--color-ink)",
  boxShadow: "0 18px 48px rgba(0,0,0,.24)"
};

const tick = { fontSize: 11, fill: AXIS };

/* -------- Linha -------- */
export function LineChartCard({
  data,
  xKey,
  series,
  height = 220,
  yDomain,
  yTickFormatter,
  xTickAngle = 0,
  xInterval = "preserveStartEnd"
}) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart
        data={data}
        margin={{ top: 12, right: 16, left: -6, bottom: 2 }}
      >
        <CartesianGrid
          stroke={GRID}
          strokeDasharray="4 6"
          vertical={false}
          opacity={0.65}
        />
        <XAxis
          dataKey={xKey}
          stroke={AXIS}
          tick={{
            ...tick,
            angle: xTickAngle,
            textAnchor: xTickAngle ? "end" : "middle"
          }}
          tickLine={false}
          axisLine={{ stroke: GRID }}
          interval={xInterval}
          minTickGap={10}
          height={xTickAngle ? 54 : 30}
          padding={{ left: 14, right: 14 }}
        />

        <YAxis
          stroke={AXIS}
          tick={tick}
          tickFormatter={yTickFormatter}
          tickLine={false}
          axisLine={false}
          width={42}
          domain={yDomain}
        />

        <Tooltip contentStyle={tooltipStyle} />
        {series.length > 1 && (
          <Legend
            wrapperStyle={{ fontSize: 12, color: AXIS }}
            iconType="circle"
          />
        )}
        {series.map((s, i) => (
          <Line
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.label}
            stroke={s.color ?? CHART_COLORS[i % CHART_COLORS.length]}
            strokeWidth={3}
            dot={false}
            activeDot={{ r: 5, strokeWidth: 3, stroke: "var(--color-surface)" }}
            connectNulls
            animationDuration={650}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

/* -------- Área -------- */
export function AreaChartCard({
  data,
  xKey,
  series,
  height = 220,
  yDomain,
  yTickFormatter,
  xTickAngle = 0,
  xInterval = "preserveStartEnd"
}) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart
        data={data}
        margin={{ top: 12, right: 16, left: -6, bottom: 2 }}
      >
        <defs>
          {series.map((s, i) => {
            const color = s.color ?? CHART_COLORS[i % CHART_COLORS.length];
            return (
              <linearGradient
                key={s.key}
                id={`grad-${s.key}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor={color} stopOpacity={0.26} />
                <stop offset="100%" stopColor={color} stopOpacity={0.04} />
              </linearGradient>
            );
          })}
        </defs>
        <CartesianGrid
          stroke={GRID}
          strokeDasharray="4 6"
          vertical={false}
          opacity={0.65}
        />
        <XAxis
          dataKey={xKey}
          stroke={AXIS}
          tick={{
            ...tick,
            angle: xTickAngle,
            textAnchor: xTickAngle ? "end" : "middle"
          }}
          tickLine={false}
          axisLine={{ stroke: GRID }}
          interval={xInterval}
          minTickGap={10}
          height={xTickAngle ? 54 : 30}
          padding={{ left: 14, right: 14 }}
        />

        <YAxis
          stroke={AXIS}
          tick={tick}
          tickFormatter={yTickFormatter}
          tickLine={false}
          axisLine={false}
          width={42}
          domain={yDomain}
        />

        <Tooltip contentStyle={tooltipStyle} />
        {series.length > 1 && (
          <Legend
            wrapperStyle={{ fontSize: 12, color: AXIS }}
            iconType="circle"
          />
        )}
        {series.map((s, i) => {
          const color = s.color ?? CHART_COLORS[i % CHART_COLORS.length];
          return (
            <Area
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              stroke={color}
              fill={`url(#grad-${s.key})`}
              strokeWidth={3}
              dot={{ r: 3, strokeWidth: 2, fill: color }}
              activeDot={{
                r: 5,
                strokeWidth: 3,
                stroke: "var(--color-surface)"
              }}
              connectNulls
              animationDuration={650}
            />
          );
        })}
      </AreaChart>
    </ResponsiveContainer>
  );
}

/* -------- Barras -------- */
export function BarChartCard({
  data,
  xKey,
  series,
  height = 220,
  horizontal = false
}) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={data}
        layout={horizontal ? "vertical" : "horizontal"}
        margin={{ top: 12, right: 16, left: horizontal ? 10 : -6, bottom: 2 }}
      >
        <CartesianGrid
          stroke={GRID}
          strokeDasharray="4 6"
          vertical={horizontal}
          horizontal={!horizontal}
          opacity={0.65}
        />
        {horizontal ? (
          <>
            <XAxis
              type="number"
              stroke={AXIS}
              tick={tick}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              type="category"
              dataKey={xKey}
              stroke={AXIS}
              tick={tick}
              tickLine={false}
              axisLine={false}
              width={92}
            />
          </>
        ) : (
          <>
            <XAxis
              dataKey={xKey}
              stroke={AXIS}
              tick={tick}
              tickLine={false}
              axisLine={{ stroke: GRID }}
              minTickGap={16}
            />
            <YAxis
              stroke={AXIS}
              tick={tick}
              tickLine={false}
              axisLine={false}
              width={38}
            />
          </>
        )}
        <Tooltip
          contentStyle={tooltipStyle}
          cursor={{ fill: "var(--color-brand-dim)" }}
        />
        {series.length > 1 && (
          <Legend
            wrapperStyle={{ fontSize: 12, color: AXIS }}
            iconType="circle"
          />
        )}
        {series.map((s, i) => (
          <Bar
            key={s.key}
            dataKey={s.key}
            name={s.label}
            fill={s.color ?? CHART_COLORS[i % CHART_COLORS.length]}
            radius={horizontal ? [0, 9, 9, 0] : [9, 9, 0, 0]}
            maxBarSize={34}
            animationDuration={650}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

/* -------- Lista de barras de progresso (nota por item) -------- */
export function NotaBarraLista({
  data,
  labelKey,
  valueKey,
  max = 10,
  labelWidth = 96
}) {
  return (
    <div className="flex flex-col gap-3.5">
      {data.map((d, i) => {
        const value = Number(d[valueKey]) || 0;
        const pct = Math.max(0, Math.min(100, (value / max) * 100));
        return (
          <div key={i} className="flex items-center gap-3">
            <span
              className="shrink-0 truncate text-[13px] text-[var(--color-ink-soft)]"
              style={{ width: labelWidth }}
            >
              {String(d[labelKey])}
            </span>
            <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-[var(--color-data-dim)]">
              <div
                className="h-full rounded-full bg-[var(--color-data)] transition-[width] duration-700 ease-out"
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="w-9 shrink-0 text-right text-[13px] font-semibold text-[var(--color-ink)]">
              {brPt(value)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* -------- Donut -------- */
export function DonutChart({ data, height = 190 }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          innerRadius="60%"
          outerRadius="84%"
          paddingAngle={3}
          cornerRadius={8}
          stroke="var(--color-surface)"
          strokeWidth={3}
        >
          {data.map(d => (
            <Cell key={d.name} fill={d.color} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={tooltipStyle}
          formatter={(v, n) => {
            const num = Number(v) || 0;
            return [
              `${num} (${total ? Math.round((num / total) * 100) : 0}%)`,
              String(n)
            ];
          }}
        />

        <Legend
          wrapperStyle={{ fontSize: 12, color: AXIS }}
          iconType="circle"
        />
      </PieChart>
    </ResponsiveContainer>
  );
}
